<template>
  <v-menu
    v-if="!storageItem.virtual"
    offset-y
    min-width="150"
    content-class="storage-item-context-menu elevation-1"
  >
    <template v-slot:activator="{ attrs, on }">
      <v-btn
        x-small
        depressed
        rounded
        color="transparent"
        v-bind="attrs"
        v-on="on"
        class="context-menu__button"
        @click="handleStorageItemDetails"
      >
        <v-icon>mdi-dots-horizontal</v-icon>
      </v-btn>
    </template>
    <v-list class="pa-0">
      <v-list-item
        class="context-menu__item"
        height="30"
        @click="handleToggleFavoriteState"
      >
        <span class="context-menu__item-text"
          >{{ $t("resource_details.favourite")
          }}<v-icon color="secondary" size="20">{{
            storageItemFavoriteState.isStorageItemFavorite
              ? "mdi-star"
              : "mdi-star-outline"
          }}</v-icon></span
        >
      </v-list-item>
      <v-list-item
        v-if="storageItem.type === FILE"
        class="context-menu__item"
        height="30"
        @click="handleDownloadStorageItem"
      >
        <span class="context-menu__item-text"
          >{{ $t("resource_details.download")
          }}<v-icon color="secondary" size="20"
            >mdi-download-outline</v-icon
          ></span
        >
      </v-list-item>

      <v-list-item
        v-if="storageItem.type === FORM && hasAccessToContextMenuItemEdit"
        class="context-menu__item"
        height="30"
        @click="handleToggleFormDialog"
      >
        <span class="context-menu__item-text">
          {{ $t("resource_details.edit") }}
          <v-icon color="secondary" size="20">mdi-pencil-outline</v-icon>
        </span>
      </v-list-item>

      <v-list-item
        v-if="storageItem.type === FORM && hasAccessToContextMenuItemPreview"
        class="context-menu__item"
        height="30"
        @click="handleToggleFormDialog"
      >
        <span class="context-menu__item-text">
          {{ $t("resource_details.preview") }}
          <v-icon color="secondary" size="20">mdi-eye</v-icon>
        </span>
      </v-list-item>

      <v-list-item
        v-if="storageItem.type === TODO && hasAccessToContextMenuItemEdit"
        class="context-menu__item"
        height="30"
        @click="handleToggleTodoDialog"
      >
        <span class="context-menu__item-text">
          {{ $t("resource_details.edit") }}
          <v-icon color="secondary" size="20">mdi-pencil-outline</v-icon>
        </span>
      </v-list-item>

      <v-list-item
        v-if="storageItem.type === TODO && hasAccessToContextMenuItemPreview"
        class="context-menu__item"
        height="30"
        @click="handleToggleTodoDialog"
      >
        <span class="context-menu__item-text">
          {{ $t("resource_details.preview") }}
          <v-icon color="secondary" size="20">mdi-eye</v-icon>
        </span>
      </v-list-item>

      <v-list-item
        v-if="hasAccessToContextMenuItem"
        class="context-menu__item"
        height="30"
        @click="handleToggleShareDialog"
      >
        <span class="context-menu__item-text"
          >{{ $t("resource_details.share")
          }}<v-icon color="secondary" size="20">mdi-share-variant</v-icon></span
        >
      </v-list-item>

      <v-list-item
        v-if="hasAccessToContextMenuItemDelete"
        class="context-menu__item"
        height="20"
        @click="handleToggleMoveStorageItem"
      >
        <span class="context-menu__item-text">
          Move
          <v-icon color="black" size="20">mdi-folder-move-outline</v-icon></span
        >
      </v-list-item>

      <v-list-item
        v-if="hasAccessToContextMenuItemDelete"
        class="context-menu__item"
        height="20"
        @click="handleToggleDeleteDialog"
      >
        <span class="context-menu__item-text"
          >{{ $t("resource_details.trash")
          }}<v-icon color="red" size="20">mdi-trash-can-outline</v-icon></span
        >
      </v-list-item>

      <v-list-item
        v-if="storageItem.type === LINK"
        class="context-menu__item"
        height="20"
        @click="handleToggleLinkDialog"
      >
        <span class="context-menu__item-text"
          >{{ $t("resource_details.open")
          }}<v-icon color="secondary" size="20">mdi-eye</v-icon></span
        >
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { ModuleTypes } from "@/store/ModuleTypes";
import { StorageItemTypes } from "@/views/academy/enums/StorageItemTypes";
import { isAdminRole } from "@/helpers/userRole.utils";
import { ShareStorageItemUserTypes } from "@/views/academy/enums/ShareStorageItemUserTypes";

export default {
  name: "StorageItemContextMenu",
  data: () => ({
    FILE: StorageItemTypes.FILE,
    FORM: StorageItemTypes.FORM,
    TODO: StorageItemTypes.TODO,
    LINK: StorageItemTypes.LINK
  }),
  props: {
    storageItem: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters(ModuleTypes.AUTH, ["userRoles"]),
    ...mapGetters(ModuleTypes.STORAGE_ITEM_DETAILS, [
      "storageItemFavoriteState"
    ]),
    ...mapGetters(ModuleTypes.STORAGE, [
      "activeOrganization",
      "storageItemDetails",
      "isAdminFoldersRootLevel"
    ]),

    hasAccessToContextMenuItem() {
      return isAdminRole(this.userRoles) && !this.$route.params?.playerId;
    },

    hasAccessToContextMenuItemDelete() {
      return this.storageItem?.accessType === ShareStorageItemUserTypes.OWNER;
    },

    hasAccessToContextMenuItemEdit() {
      return this.storageItem?.accessType !== ShareStorageItemUserTypes.VIEWER;
    },

    hasAccessToContextMenuItemPreview() {
      return this.storageItem?.accessType === ShareStorageItemUserTypes.VIEWER;
    }
  },
  methods: {
    ...mapMutations(ModuleTypes.STORAGE, [
      "toggleShareStorageItemDialog",
      "toggleDeleteStorageItemDialog",
      "toggleFormDialog",
      "toggleTodoDialog",
      "toggleStorageLinkModal",
      "toggleMoveStorageItemModal"
    ]),

    ...mapMutations(ModuleTypes.ROOT, ["closeRightSidebar"]),
    ...mapActions(ModuleTypes.STORAGE, ["setStorageItemDetails"]),
    ...mapActions(ModuleTypes.STORAGE_ITEM_DETAILS, [
      "toggleStorageItemFavoriteState",
      "fetchStorageItemPresignedUrl"
    ]),

    handleStorageItemDetails() {
      this.setStorageItemDetails({
        storageItem: this.storageItem,
        isContextMenu: true
      });
    },

    handleToggleFavoriteState() {
      this.toggleStorageItemFavoriteState({
        state: this.storageItemFavoriteState,
        playerId: this.$route.params?.playerId,
        orgAdmins: this.isAdminFoldersRootLevel
      });
    },

    handleDownloadStorageItem() {
      this.fetchStorageItemPresignedUrl({
        storageItemDetails: this.storageItemDetails,
        isDownloadStorageItem: true
      });
    },

    handleToggleShareDialog() {
      this.toggleShareStorageItemDialog({
        isShareStorageItemDialogOpened: true
      });
      this.closeRightSidebar();
    },

    handleToggleDeleteDialog() {
      this.toggleDeleteStorageItemDialog({
        isDeleteStorageItemDialogOpened: true
      });
    },
    handleToggleMoveStorageItem() {
      this.toggleMoveStorageItemModal({ isMoveStorageItemModalOpen: true });
    },
    handleToggleFormDialog() {
      this.toggleFormDialog({
        isFormDialogOpened: true,
        isFormCreateDialog: false,
        isFormDialogPreview:
          this.storageItem?.accessType === ShareStorageItemUserTypes.VIEWER
      });
    },
    handleToggleTodoDialog() {
      this.toggleTodoDialog({
        isTodoDialogOpened: true,
        isTodoCreateDialog: false,
        isTodoDialogPreview:
          this.storageItem?.accessType === ShareStorageItemUserTypes.VIEWER
      });
    },
    handleToggleLinkDialog() {
      return this.toggleStorageLinkModal({ linkModalOpen: true });
    }
  }
};
</script>

<style>
.context-menu__item-text {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.v-menu__content {
  margin-top: 2px;
  border-radius: 10px;
}

.context-menu__button {
  display: none !important;
}

.storage-list__item:hover .context-menu__button,
.storage-list__item--active .context-menu__button {
  display: flex !important;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 5px;
  right: 10px;
  color: var(--dark-color);
  z-index: 1;
}

.storage-item-context-menu.v-menu__content {
  z-index: 100 !important;
}
</style>
